/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { computed, Injectable, signal } from '@angular/core';
import { IsolationQueryResults } from '@app/types/LineIsolation';
import { GeometryCategory, MapQueryPropertyFilter } from '@app/types/Map';
import { FeatureSelection } from '@app/types/MapFeature';
import { AppConfigState } from '@core/state/appConfig/app-config.state';
import { FeatureService } from '@map/services/feature.service';

import { MapConfigService } from '@map/services/map-config.service';
import { MapService } from '@map/services/map.service';
import { QueryService } from '@map/services/query.service';
import { MergeLayerDisplayFeatures, SetLayerDisplayFeatures } from '@map/state/active-map.actions';
import { DisplayFeaturePayload } from '@map/state/active-map.model';
import { ClearSelection, MergeSelection, SetSelection } from '@map/state/selection.actions';
import { Actions, ofActionSuccessful, Store } from '@ngxs/store';
import { RestService } from '@shared/services/rest.service';
import { forkJoin, Observable, of, Subscription } from 'rxjs';

import { concatMap, map, mergeMap, shareReplay, switchMap, take } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class LineIsolationService {
    isolationData = computed(() => this.isolationDataInternal());
    hasIsolationData = computed(() => !!this.isolationDataInternal());

    private isolationDataInternal = signal<IsolationQueryResults>(null);
    private mapName: string;
    private layerName: string;
    private unsubscriber: Subscription;

    constructor(
        private rest: RestService,
        private mapConfig: MapConfigService,
        private mapService: MapService,
        private queryService: QueryService,
        private featureService: FeatureService,
        private actions$: Actions,
        private store: Store
    ) {}

    public query(mapName: string, layerName: string, features: any[]) {
        const url = `isolation/query/${ mapName }/${ layerName }`;

        const lineLayer = this.mapConfig.getLayer(layerName);
        const nodeLayer = this.mapConfig.getLayer(this.isolationLayers[mapName][layerName].nodeLayer);
        const parcelLayer = this.isolationLayers[mapName][layerName]?.parcelLayer
            ? this.mapConfig.getLayer(this.isolationLayers[mapName][layerName].parcelLayer)
            : null;

        const source = this.mapService.getVectorLayer('isolation').getSource();

        const query$ = this.rest.apiGet(url, { featureId: features.map(String) }).pipe(
            map((queryData: IsolationQueryResults) => {
                this.mapName = mapName;
                this.layerName = layerName;
                this.isolationDataInternal.set(queryData);

                // get rid of the current map selection
                this.queryService.clearSelection();
                return queryData;
            }),
            shareReplay()
        );

        const lineQuery$ = query$.pipe(
            mergeMap((_: any) => {
                const lineFilter: MapQueryPropertyFilter = {
                    filter: {
                        property: this.isolationLayers[mapName][layerName].lineFeatureField.name,
                        values: this.isolationData().lines
                    },
                    type: 'property'
                };

                return this.queryService.queryLayers$([ lineLayer ], [], lineFilter, source, false, false, true);
            }),
            switchMap((lineFeatures) => {
                const featureIds = lineFeatures.reduce(
                    (acc: string[], featureObject) => acc.concat.apply(acc, featureObject.featureIds),
                    []
                );
                const styles = this.mapConfig.getStyles('isolation', GeometryCategory.Line);
                this.featureService.updateFeatureStyle(featureIds, styles);
                const payload: DisplayFeaturePayload = {
                    displayLayer: 'isolation',
                    featureIds
                };
                return this.store.dispatch(new MergeLayerDisplayFeatures(payload));
            })
        );

        const nodeQuery$ = query$.pipe(
            mergeMap((_: any) => {
                const nodeFilter: MapQueryPropertyFilter = {
                    filter: {
                        property: this.isolationLayers[mapName][layerName].nodeFeatureField.name,
                        values: this.isolationData().nodes
                    },
                    type: 'property'
                };

                return this.queryService.queryLayers$([ nodeLayer ], [], nodeFilter, source, false, false, true);
            }),
            switchMap((nodeFeatures) => {
                const featureIds = nodeFeatures.reduce(
                    (acc: string[], featureObject) => acc.concat.apply(acc, featureObject.featureIds),
                    []
                );
                const styles = this.mapConfig.getStyles('isolation', GeometryCategory.Point);
                this.featureService.updateFeatureStyle(featureIds, styles);
                const payload: DisplayFeaturePayload = {
                    displayLayer: 'isolation',
                    featureIds
                };
                return this.store.dispatch(new MergeLayerDisplayFeatures(payload));
            })
        );

        const parcelQuery$ = !!parcelLayer
            ? query$.pipe(
                mergeMap((_: any) => {
                    const parcelFilter: MapQueryPropertyFilter = {
                        filter: {
                            property: this.isolationLayers[mapName][layerName]?.parcelFeatureField.name,
                            values: this.isolationData().parcels
                        },
                        type: 'property'
                    };

                    return this.queryService.queryLayers$([ parcelLayer ], [], parcelFilter, source, false, false, true);
                }),
                switchMap((parcelFeatures) => {
                    const featureIds = parcelFeatures.reduce(
                        (acc: string[], featureObject) => acc.concat.apply(acc, featureObject.featureIds),
                        []
                    );
                    const styles = this.mapConfig.getStyles('isolation', GeometryCategory.Point);
                    this.featureService.updateFeatureStyle(featureIds, styles);
                    const payload: DisplayFeaturePayload = {
                        displayLayer: 'isolation',
                        featureIds
                    };
                    return this.store.dispatch(new MergeLayerDisplayFeatures(payload));
                })
            )
            : of(null);

        this.unsubscriber = forkJoin([ lineQuery$, nodeQuery$, parcelQuery$ ])
            .pipe(
                concatMap(() => this.actions$),
                ofActionSuccessful(ClearSelection, SetSelection, MergeSelection),
                take(1)
            )
            .subscribe(() => this.clearIsolationData());
    }

    public selectFeatures() {
        // we're going to make a selection based on isolation data, so remove the
        // action handlers
        this.unsubscriber.unsubscribe();

        // get rid of the current selection
        this.queryService.clearSelection();

        const lineFilter: MapQueryPropertyFilter = {
            filter: {
                property: this.isolationLayers[this.mapName][this.layerName].lineFeatureField.name,
                values: this.isolationDataInternal().lines
            },
            type: 'property'
        };
        const lineQuery$ = this.queryService.doQuery(lineFilter, true, false, this.layerName);

        const nodeFilter: MapQueryPropertyFilter = {
            filter: {
                property: this.isolationLayers[this.mapName][this.layerName].nodeFeatureField.name,
                values: this.isolationDataInternal().nodes
            },
            type: 'property'
        };
        const nodeQuery$ = this.queryService.doQuery(
            nodeFilter,
            true,
            false,
            this.isolationLayers[this.mapName][this.layerName].nodeLayer
        );

        const queries = [ lineQuery$, nodeQuery$ ];

        if (this.isolationLayers[this.mapName][this.layerName]?.parcelLayer) {
            const parcelFilter: MapQueryPropertyFilter = {
                filter: {
                    property: this.isolationLayers[this.mapName][this.layerName].parcelFeatureField.name,
                    values: this.isolationDataInternal().parcels
                },
                type: 'property'
            };
            const parcelQuery$ = this.queryService.doQuery(
                parcelFilter,
                true,
                false,
                this.isolationLayers[this.mapName][this.layerName].parcelLayer
            );
            queries.push(parcelQuery$);
        }

        return forkJoin(queries);
    }

    public getSelectedFeatures(mapName: string, layerName: string) {
        const layerIsolationConfig = this.isolationLayers[mapName][layerName];
        return this.queryService
            .getSelection$(layerName)
            .pipe(
                map((selections: FeatureSelection[]) =>
                    selections.length ? selections.map(selection => selection.fields[layerIsolationConfig.lineFeatureField.name]) : []
                )
            );
    }

    public isIsolationEnabled(mapName: string, layerName: string): Observable<boolean> {
        return this.queryService.getSelection$(layerName).pipe(
            map((selection) => {
                let enabled = false;
                if (this.isolationLayers) {
                    enabled =
                        !!this.isolationLayers[mapName] &&
                        !!this.isolationLayers[mapName][layerName] &&
                        selection.length > 0;
                }
                return enabled;
            })
        );
    }

    public clearIsolationData() {
        this.mapName = null;
        this.layerName = null;
        this.isolationDataInternal.set(null);
        // this.mapService.getVectorLayer('isolation').getSource().clear();
        this.store.dispatch(
            new SetLayerDisplayFeatures({
                displayLayer: 'isolation',
                featureIds: []
            })
        );
    }

    get isolationLayers() {
        return this.store.selectSnapshot(AppConfigState.isolation);
    }
}
